import { NODE_TYPES, NODE_TYPE_ORDER, TYPE_ALIASES, type NodeTypeIri } from './node-types.js';

const FLOW_ELEMENTS: readonly NodeTypeIri[] = [
  NODE_TYPES.Phase,
  NODE_TYPES.Stage,
  NODE_TYPES.WorkPractice,
  NODE_TYPES.Activity,
];

/**
 * What each reference term is allowed to point at. This is the table the
 * validator uses to catch `performedBy: tmpl:HLD` — a reference that resolves
 * but means nothing. Every term of kind `id` in `TERMS` must appear here; a test
 * asserts that, so adding a term without a target is a build failure.
 */
export const REFERENCE_TARGETS = {
  hasPhase: [NODE_TYPES.Phase],
  hasStage: [NODE_TYPES.Stage],
  hasWorkPractice: [NODE_TYPES.WorkPractice],
  hasActivity: [NODE_TYPES.Activity],
  hasRole: [NODE_TYPES.Role],
  hasGate: TYPE_ALIASES.gate,
  hasDeliverable: TYPE_ALIASES.deliverable,
  hasLens: TYPE_ALIASES.lens,
  hasCeremony: TYPE_ALIASES.ceremony,
  hasTool: [NODE_TYPES.Tool],
  hasAsset: [NODE_TYPES.Asset],
  hasAgent: [NODE_TYPES.Agent],
  hasAgentSkill: [NODE_TYPES.AgentSkill],
  hasMcpServer: [NODE_TYPES.McpServer],
  inPhase: [NODE_TYPES.Phase],
  inWorkPractice: [NODE_TYPES.WorkPractice],
  parentPractice: [NODE_TYPES.WorkPractice],
  precedes: FLOW_ELEMENTS,
  dependsOn: FLOW_ELEMENTS,
  loopsTo: FLOW_ELEMENTS,
  performedBy: [NODE_TYPES.Role],
  produces: TYPE_ALIASES.deliverable,
  consumes: TYPE_ALIASES.deliverable,
  usesLens: TYPE_ALIASES.lens,
  usesTool: [NODE_TYPES.Tool],
  usesAsset: [NODE_TYPES.Asset],
  usesAgent: [NODE_TYPES.Agent],
  usesAgentSkill: [NODE_TYPES.AgentSkill],
  governedByGate: TYPE_ALIASES.gate,
  communication: TYPE_ALIASES.ceremony,
  agentRole: [NODE_TYPES.Role],
  humanRole: [NODE_TYPES.Role],
  implementsRole: [NODE_TYPES.Role],
  realisedByAgent: [NODE_TYPES.Agent],
  hasSkill: [NODE_TYPES.AgentSkill],
  usesMcpServer: [NODE_TYPES.McpServer],
  variantOf: [NODE_TYPES.Method],
  templateOf: TYPE_ALIASES.deliverable,
  hasTemplateVersion: [NODE_TYPES.TemplateVersion],
  currentTemplateVersion: [NODE_TYPES.TemplateVersion],
  templateOverride: [NODE_TYPES.TemplateVersion],
  /**
   * A client brings catalogue entries and nothing else: the spine of the method is
   * the platform's to define, and a session that could contribute a phase would be
   * authoring a method rather than adopting one.
   */
  clientContributions: [
    NODE_TYPES.Tool,
    NODE_TYPES.Asset,
    NODE_TYPES.Agent,
    NODE_TYPES.AgentSkill,
    NODE_TYPES.McpServer,
    ...TYPE_ALIASES.deliverable,
  ],
  /** A tailoring decision may target any element of the method. */
  appliesTo: NODE_TYPE_ORDER,
  decidedBy: [NODE_TYPES.Role],
} as const satisfies Record<string, readonly NodeTypeIri[]>;

export type ReferenceTerm = keyof typeof REFERENCE_TARGETS;

/** Terms that describe containment; used for order and orphan checking. */
export const CONTAINMENT_TERMS = [
  'hasPhase',
  'hasStage',
  'hasWorkPractice',
  'hasActivity',
] as const satisfies readonly ReferenceTerm[];

export type ContainmentTerm = (typeof CONTAINMENT_TERMS)[number];
